/**
 * Tells which auctions are running on the live auction site (live.skanfil.no) right now.
 * While an auction is live, bidding on skanfil.no for its lots is replaced by a link to the live site.
 *
 * One instance per page. The server renders the state into the page (script#live-auction-schedule), so it is known
 * at the very first render and nothing is requested before the page shows. The live site signals the start and the
 * end of a session, and the server pushes both through SignalR:
 *  - start: a page that is already open swaps the bid controls for the link, without a reload.
 *  - end: the auction is over, exactly like a normal auction after its bidding ended. Every lot of it on the page
 *    fetches itself again (the handler registered with watch()) and shows the result, as a lot does when its own
 *    countdown reaches zero. Nothing reloads and nothing waits.
 *  - start time: a session can have one, for the case that the start signal never arrives. When it passes, the
 *    server counts the session as started, so the page reads the state again at that moment.
 *  - registration: an auction was added to, changed in or deleted from the Operator Center. The page reads the state
 *    again, so the countdowns of its lots go away (or come back) without a reload.
 * An auction can also have a latest end, set in the Operator Center for the case that the end signal never arrives.
 * When it passes, the session counts as ended, exactly as if the end signal had come.
 * The state is also re-read in the background every few minutes, in case a push was missed.
 */
import getAuctionHub from "../getAuctionHub";
const SCHEDULE_URL = "/api/live-auction/schedule";
const START_URL = "/api/live-auction/auth/start";
const EMBEDDED_SCHEDULE_ID = "live-auction-schedule";
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const RETRY_INTERVAL_MS = 30 * 1000;
// Bid controls wait for the first answer, so a hanging request must not hide them for long.
const REQUEST_TIMEOUT_MS = 5 * 1000;
// The link to the live site stays until the lots have fetched their results, so a lot that is already closed never
// flashes its bid controls. If a lot does not answer in this time, the page moves on without it.
const MAX_RESULTS_WAIT_MS = 4 * 1000;
// Timers drift in background tabs and overflow after ~24 days, so the latest-end timer never sleeps longer than this.
const MAX_TIMER_MS = 30 * 1000;
class LiveAuctionSchedule {
    constructor() {
        this.liveIds = new Set();
        this.registeredIds = new Set();
        // Latest end of the live auctions that have one, in server time (ms).
        this.endsAt = new Map();
        this.clockOffsetMs = 0;
        this.endedHandlers = new Map();
        this.finishing = new Set();
        this.listeners = new Set();
        this.initialized = false;
        // The first state the server reported is the starting point, not a change to announce.
        this.initializedState = false;
        this.ready = false;
        this.lastLoadedAt = 0;
        // Start times of waiting sessions, in server time (ms).
        this.scheduledStarts = [];
        this.refresh = () => {
            window.clearTimeout(this.refreshTimer);
            const requestedAt = Date.now();
            const abort = new AbortController();
            const timeout = window.setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);
            fetch(SCHEDULE_URL, { cache: "no-store", credentials: "same-origin", signal: abort.signal })
                .finally(() => window.clearTimeout(timeout))
                .then((res) => {
                if (!res.ok)
                    throw new Error(`Live auction schedule: ${res.status}`);
                return res.json();
            })
                .then((schedule) => {
                const respondedAt = Date.now();
                this.lastLoadedAt = respondedAt;
                this.refreshTimer = window.setTimeout(this.refresh, REFRESH_INTERVAL_MS);
                // The server time was taken somewhere during the request, so assume the middle of it.
                this.apply(schedule, (requestedAt + respondedAt) / 2);
                this.setReady();
            })
                .catch((error) => {
                // Fail open: bidding on skanfil.no works as before until the state can be read.
                console.error(error);
                this.refreshTimer = window.setTimeout(this.refresh, RETRY_INTERVAL_MS);
                this.setReady();
            });
        };
        this.onVisibilityChange = () => {
            if (document.visibilityState !== "visible")
                return;
            // Timers are throttled and pushes can be missed while the tab sleeps: catch up when it comes back.
            this.scheduleEndTimer();
            // A start time that passed while the tab slept has not been applied here yet (its timer was throttled): read the
            // state at once, which also arms the timer for the next start time.
            const startPassed = this.scheduledStarts.some((startsAt) => startsAt <= this.now());
            if (startPassed || Date.now() - this.lastLoadedAt > RETRY_INTERVAL_MS) {
                this.refresh();
            }
            else {
                this.scheduleStartTimer();
            }
        };
    }
    /**
     * True once the state is known. Bid controls wait for it, so a live lot never shows them even for a moment.
     */
    isReady() {
        this.init();
        return this.ready;
    }
    /**
     * True while the auction's live session is running. Unknown auctions (and an unavailable state) are never
     * live, so the regular bidding keeps working.
     */
    isLive(auctionId) {
        this.init();
        return auctionId != null && this.liveIds.has(auctionId);
    }
    /**
     * True while the auction is registered for the live site and has not ended, before and during its live session.
     * Nobody knows how long that lasts, so its lots show no countdown.
     */
    isRegistered(auctionId) {
        this.init();
        return auctionId != null && this.registeredIds.has(auctionId);
    }
    /**
     * Registers what a lot of this auction does when the auction ends: fetch itself again, like it does when its own
     * countdown reaches zero. Returns the function that undoes it.
     */
    watch(auctionId, onEnded) {
        var _a;
        this.init();
        if (auctionId == null || !onEnded)
            return () => undefined;
        const handlers = (_a = this.endedHandlers.get(auctionId)) !== null && _a !== void 0 ? _a : new Set();
        handlers.add(onEnded);
        this.endedHandlers.set(auctionId, handlers);
        return () => {
            handlers.delete(onEnded);
            if (handlers.size === 0) {
                this.endedHandlers.delete(auctionId);
            }
        };
    }
    /**
     * Calls the listener when the answer of isLive(), isRegistered() or isReady() may have changed.
     * Returns the unsubscribe function.
     */
    subscribe(listener) {
        this.init();
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    init() {
        if (this.initialized)
            return;
        this.initialized = true;
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        const embedded = this.readEmbedded();
        if (embedded) {
            this.apply(embedded, Date.now());
            this.ready = true;
            this.refreshTimer = window.setTimeout(this.refresh, REFRESH_INTERVAL_MS);
            // A latest end and a start time are timed in the browser, and the server time embedded in the page is as old
            // as the page is (the scripts may load for a while), which would make the timers late by that much. Ask
            // once for a fresh one, in the background. Pages without a latest end or a start time make no request.
            if (this.endsAt.size > 0 || this.scheduledStarts.length > 0) {
                this.refresh();
            }
        }
        else {
            this.refresh();
        }
        this.listenForSignals();
    }
    now() {
        return Date.now() + this.clockOffsetMs;
    }
    readEmbedded() {
        var _a;
        try {
            const text = (_a = document.getElementById(EMBEDDED_SCHEDULE_ID)) === null || _a === void 0 ? void 0 : _a.textContent;
            if (!text)
                return null;
            const data = JSON.parse(text);
            return Array.isArray(data === null || data === void 0 ? void 0 : data.liveAuctions) && data.serverTimeUtc ? data : null;
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }
    listenForSignals() {
        getAuctionHub()
            .then((hub) => {
            hub === null || hub === void 0 ? void 0 : hub.onLiveAuctionChanged((e) => this.change(e.auctionId, e.isLive));
            hub === null || hub === void 0 ? void 0 : hub.onLiveAuctionRegistrationChanged(() => this.refresh());
        })
            .catch((error) => console.error(error));
    }
    /**
     * Takes over the state the server reported. The first call has nothing to compare with, so it announces nothing.
     */
    apply(schedule, receivedAt) {
        var _a, _b;
        // The server clock is what the latest ends are measured on.
        this.clockOffsetMs = Date.parse(schedule.serverTimeUtc) - receivedAt;
        this.endsAt = new Map(schedule.liveAuctions
            .filter((x) => x.endsAtUtc)
            .map((x) => [x.auctionId, Date.parse(x.endsAtUtc)]));
        const ids = schedule.liveAuctions.map((x) => x.auctionId);
        const registered = new Set([...((_a = schedule.registeredAuctionIds) !== null && _a !== void 0 ? _a : []), ...ids]);
        if (!this.initializedState) {
            this.initializedState = true;
            this.liveIds = new Set(ids);
            this.registeredIds = registered;
        }
        else {
            const serverLive = new Set(ids);
            const started = ids.filter((id) => !this.liveIds.has(id));
            const ended = Array.from(this.liveIds).filter((id) => !serverLive.has(id));
            // An auction that just ended stays live and registered until its lots have fetched their results.
            ended.forEach((id) => registered.add(id));
            const registeredChanged = registered.size !== this.registeredIds.size || Array.from(registered).some((id) => !this.registeredIds.has(id));
            this.registeredIds = registered;
            if (started.length > 0) {
                this.liveIds = new Set([...Array.from(this.liveIds), ...started]);
            }
            if (started.length > 0 || registeredChanged) {
                this.notify();
            }
            ended.forEach((id) => void this.finish(id));
        }
        this.scheduledStarts = ((_b = schedule.scheduledStarts) !== null && _b !== void 0 ? _b : []).map((x) => Date.parse(x.startsAtUtc));
        this.scheduleEndTimer();
        this.scheduleStartTimer();
    }
    change(auctionId, isLive) {
        if (isLive) {
            if (this.liveIds.has(auctionId))
                return;
            this.liveIds = new Set(this.liveIds).add(auctionId);
            this.registeredIds = new Set(this.registeredIds).add(auctionId);
            this.notify();
            // The push says nothing about a latest end: ask for it.
            this.refresh();
            return;
        }
        if (this.liveIds.has(auctionId)) {
            void this.finish(auctionId);
        }
    }
    /**
     * The auction is over. Its lots fetch themselves again, like a lot does when its countdown reaches zero, and only
     * when they have the result the page stops treating the auction as live.
     */
    async finish(auctionId) {
        var _a;
        if (this.finishing.has(auctionId))
            return;
        this.finishing.add(auctionId);
        try {
            const handlers = Array.from((_a = this.endedHandlers.get(auctionId)) !== null && _a !== void 0 ? _a : []);
            if (handlers.length > 0) {
                const results = Promise.all(handlers.map(async (handler) => {
                    try {
                        await handler();
                    }
                    catch (error) {
                        console.error(error);
                    }
                }));
                await Promise.race([results, new Promise((resolve) => window.setTimeout(resolve, MAX_RESULTS_WAIT_MS))]);
            }
            const live = new Set(this.liveIds);
            live.delete(auctionId);
            this.liveIds = live;
            const registered = new Set(this.registeredIds);
            registered.delete(auctionId);
            this.registeredIds = registered;
            this.endsAt.delete(auctionId);
            this.notify();
        }
        finally {
            this.finishing.delete(auctionId);
        }
    }
    /**
     * Ends the sessions whose latest end passed, as if the end signal had come, and arms the timer for the next one.
     */
    scheduleEndTimer() {
        window.clearTimeout(this.endTimer);
        const now = this.now();
        let next = Infinity;
        Array.from(this.endsAt.entries()).forEach(([auctionId, endsAt]) => {
            if (!this.liveIds.has(auctionId))
                return;
            if (endsAt <= now) {
                this.change(auctionId, false);
            }
            else if (endsAt < next) {
                next = endsAt;
            }
        });
        if (next !== Infinity) {
            // A few ms after the moment, so it has certainly passed when the timer fires.
            this.endTimer = window.setTimeout(() => this.scheduleEndTimer(), Math.min(next - now + 20, MAX_TIMER_MS));
        }
    }
    /**
     * When the next start time passes, the server counts that session as started: read the state again then, which
     * swaps the bid controls for the link like a start signal does.
     */
    scheduleStartTimer() {
        window.clearTimeout(this.startTimer);
        const now = this.now();
        const upcoming = this.scheduledStarts.filter((startsAt) => startsAt > now);
        if (upcoming.length === 0)
            return;
        const next = Math.min(...upcoming);
        // A few ms after the moment, so it has certainly passed on the server when the request arrives.
        this.startTimer = window.setTimeout(() => {
            if (this.now() >= next) {
                this.refresh();
            }
            else {
                this.scheduleStartTimer();
            }
        }, Math.min(next - now + 100, MAX_TIMER_MS));
    }
    notify() {
        this.listeners.forEach((listener) => listener());
    }
    setReady() {
        if (this.ready)
            return;
        this.ready = true;
        this.notify();
    }
}
export const liveAuctionSchedule = new LiveAuctionSchedule();
/**
 * Address that signs the member in on the live site and lands on the lot.
 * The server needs a signed-in member: guests are sent to the login first and continue from there.
 */
export function getLiveAuctionUrl(lotId, price) {
    const query = new URLSearchParams({ lotId: lotId.toString() });
    if (price != null && price > 0) {
        query.set("price", Math.floor(price).toString());
    }
    return `${START_URL}?${query.toString()}`;
}
